import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import CharacterCounter from '../components/CharacterCounter';
import Footer from '../components/Footer';
import { loadState } from '../utils/stateManager';
import { extractIdentity, generateKeyvaultName } from '../utils/bicepGenerator';
import './Page4KeyVault.css';

function Page4KeyVault() {
  const navigate = useNavigate();
  const [kvName, setKvName] = useState('');
  const [kvValid, setKvValid] = useState(true);
  const [kvErrors, setKvErrors] = useState([]);
  const [subscription, setSubscription] = useState('');

  useEffect(() => {
    const state = loadState();
    const sub = state.page2Discovery?.subscription || '';
    setSubscription(sub);
    if (sub) {
      const { front9, back9 } = extractIdentity(sub);
      const result = generateKeyvaultName(front9, back9, 'kv_infrastructure');
      setKvName(result.name);
      setKvValid(result.isValid);
      setKvErrors(result.errors);
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleBack = () => {
    navigate('/page3');
  };

  const handleContinue = () => {
    navigate('/page5');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={4} totalPages={9} />
      </div>

      <div className="page-content">
        <div className="section-header">
          <h2>Step 4: KeyVault - Secure Password Storage</h2>
          <span className="badge-required">REQUIRED</span>
        </div>

        <div className="why-keyvault">
          <h3>🔐 Why KeyVault is Important</h3>
          <div className="benefits-grid">
            <div className="benefit-card">
              <span className="benefit-icon">🔒</span>
              <h4>Security</h4>
              <p>Stores VM admin password securely in Azure's HSM-backed vault</p>
            </div>

            <div className="benefit-card">
              <span className="benefit-icon">👥</span>
              <h4>No Password Sharing</h4>
              <p>Team members retrieve passwords directly—no email/chat sharing needed</p>
            </div>

            <div className="benefit-card">
              <span className="benefit-icon">✅</span>
              <h4>Compliance</h4>
              <p>Meets SOC2, HIPAA, PCI-DSS requirements for credential management</p>
            </div>

            <div className="benefit-card">
              <span className="benefit-icon">🔄</span>
              <h4>Easy Rotation</h4>
              <p>Change passwords centrally without updating scripts or documentation</p>
            </div>
          </div>
        </div>

        <div className="cost-section">
          <h3>💰 Cost</h3>
          <p className="cost-value">~$0.03/month</p>
          <p className="cost-note">(Negligible cost - Essential security investment)</p>
        </div>

        <div className="naming-section">
          <h3>🏷️ Generated Name</h3>
          {kvName ? (
            <>
              <div className="resource-card">
                <div className="resource-header">
                  <span className={`status-icon ${kvValid ? 'valid' : 'invalid'}`}>
                    {kvValid ? '✅' : '❌'}
                  </span>
                  <strong>Your KeyVault Name:</strong>
                </div>
                <CharacterCounter value={kvName} maxLength={24} />
                {!kvValid && kvErrors.length > 0 && (
                  <p className="error-message">{kvErrors.join(', ')}</p>
                )}
              </div>
              <p className="pattern-explanation">
                Pattern: <code>kv1&#123;F9&#125;-&#123;B9&#125;</code><br />
                <strong>F9</strong> = First 9 alphanumeric characters of "<em>{subscription}</em>"<br />
                <strong>B9</strong> = Last 9 alphanumeric characters of "<em>{subscription}</em>"
              </p>
            </>
          ) : (
            <p className="pattern-explanation">
              Pattern: <code>kv1&#123;F9&#125;-&#123;B9&#125;</code><br />
              Go back to Step 2 and enter your subscription name to see the generated KeyVault name.
            </p>
          )}
        </div>

        <div className="usage-section">
          <h3>🔧 How to Use Your KeyVault</h3>

          <div className="command-block">
            <h4>Retrieve Password for RDP/SSH Access</h4>
            <p className="command-description">Use this command to get the password when connecting to your VM:</p>
            <div className="cli-command">
              <code>az keyvault secret show --vault-name &#123;kvname&#125; --name vm-admin-password --query value -o tsv</code>
              <button onClick={() => copyToClipboard('az keyvault secret show --vault-name {kvname} --name vm-admin-password --query value -o tsv')}>
                Copy
              </button>
            </div>
            <p className="command-note">Replace <code>&#123;kvname&#125;</code> with your KeyVault name (shown in final deployment)</p>
          </div>

          <div className="command-block">
            <h4>Change Password (Recommended Every 90 Days)</h4>
            <p className="command-description">Rotate your VM password securely:</p>

            <div className="cli-command-multiline">
              <div className="cli-title">PowerShell:</div>
              <pre><code># Generate new random password
$newPassword = -join ((48..57) + (65..90) + (97..122) | Get-Random -Count 20 | ForEach-Object &#123;[char]$_&#125;)

# Store in KeyVault
az keyvault secret set --vault-name &#123;kvname&#125; --name vm-admin-password --value $newPassword

# Update VM password
az vm user update --resource-group &#123;rg&#125; --name &#123;vmname&#125; --username vmadmin --password $newPassword

# Display new password
Write-Host "New password stored: $newPassword"</code></pre>
              <button onClick={() => copyToClipboard(`# Generate new random password\n$newPassword = -join ((48..57) + (65..90) + (97..122) | Get-Random -Count 20 | ForEach-Object {[char]$_})\n\n# Store in KeyVault\naz keyvault secret set --vault-name {kvname} --name vm-admin-password --value $newPassword\n\n# Update VM password\naz vm user update --resource-group {rg} --name {vmname} --username vmadmin --password $newPassword\n\n# Display new password\nWrite-Host "New password stored: $newPassword"`)}>
                Copy
              </button>
            </div>

            <div className="cli-command-multiline">
              <div className="cli-title">Bash:</div>
              <pre><code># Generate new random password
newPassword=$(openssl rand -base64 20)

# Store in KeyVault
az keyvault secret set --vault-name &#123;kvname&#125; --name vm-admin-password --value "$newPassword"

# Update VM password
az vm user update --resource-group &#123;rg&#125; --name &#123;vmname&#125; --username vmadmin --password "$newPassword"

# Display new password
echo "New password stored: $newPassword"</code></pre>
              <button onClick={() => copyToClipboard(`# Generate new random password\nnewPassword=$(openssl rand -base64 20)\n\n# Store in KeyVault\naz keyvault secret set --vault-name {kvname} --name vm-admin-password --value "$newPassword"\n\n# Update VM password\naz vm user update --resource-group {rg} --name {vmname} --username vmadmin --password "$newPassword"\n\n# Display new password\necho "New password stored: $newPassword"`)}>
                Copy
              </button>
            </div>
          </div>
        </div>

        <div className="best-practices">
          <h3>📋 Best Practices</h3>
          <ul>
            <li>✓ Rotate passwords every 90 days (or per your security policy)</li>
            <li>✓ Grant KeyVault access only to team members who need VM access</li>
            <li>✓ Use Azure RBAC for KeyVault permissions (instead of access policies)</li>
            <li>✓ Enable audit logging to track who accesses secrets</li>
            <li>✓ Never store passwords in scripts, documentation, or chat messages</li>
          </ul>
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn-primary" onClick={handleContinue}>
          Next: Storage Account →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page4KeyVault;
